const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export interface ChatCompletionMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export const openaiService = {
  async getChatCompletion(
    messages: ChatCompletionMessage[],
    onChunk?: (text: string) => void,
    guideType?: string
  ): Promise<string> {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      throw new Error('Supabase configuration is missing.');
    }

    const apiUrl = `${SUPABASE_URL}/functions/v1/sales-coach`;

    const payload: Record<string, any> = { messages };
    if (guideType) {
      payload.guide_type = guideType;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to get response: ${error}`);
      }

    if (onChunk && response.body) {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';
      let lineBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        lineBuffer += chunk;
        const lines = lineBuffer.split('\n');
        lineBuffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data: ')) continue;
          const data = trimmed.slice(6);
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            const content = parsed.choices?.[0]?.delta?.content || '';
            if (content) {
              fullResponse += content;
              onChunk(fullResponse);
            }
          } catch (e) {
            // Incomplete JSON will be caught on next chunk via lineBuffer
          }
        }
      }

      if (lineBuffer.trim().startsWith('data: ')) {
        const data = lineBuffer.trim().slice(6);
        if (data !== '[DONE]') {
          try {
            const parsed = JSON.parse(data);
            const content = parsed.choices?.[0]?.delta?.content || '';
            if (content) {
              fullResponse += content;
              onChunk(fullResponse);
            }
          } catch (e) {
            // Final incomplete line
          }
        }
      }

      return fullResponse;
    }

      const text = await response.text();
      return text;
    } catch (error: any) {
      throw new Error(`Failed to fetch: ${error.message}`);
    }
  },

  isConfigured(): boolean {
    return !!SUPABASE_URL && !!SUPABASE_ANON_KEY;
  },
};
